import { useReducer } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';

const useStripeValidations = () => {
  // Initialize Stripe and Elements
  const stripe = useStripe();
  const elements = useElements();

  const initialState = {
    cardNumber: {
      completed: false,
      empty: true,
      valid: true,
      error: '',
    },
    cardExpiry: {
      completed: false,
      empty: true,
      valid: true,
      error: '',
    },
    cardCVC: {
      completed: false,
      empty: true,
      valid: true,
      error: '',
    },
  };

  const fieldNameMapping = {
    cardNumber: 'CARD NUMBER',
    cardExpiry: 'EXPIRATION DATE',
    cardCVC: 'CVC',
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'INITIALIZE':
        return {
          ...initialState,
        };
      case 'SET_FIELD':
        return {
          ...state,
          [action.payload.fieldName]: {
            ...state[action.payload.fieldName],
            ...action.payload.value,
          },
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleCardNumberChange = (event) => {
    const { complete, empty, error } = event;
    dispatch({
      type: 'SET_FIELD',
      payload: {
        fieldName: 'cardNumber',
        value: {
          completed: complete,
          empty,
          valid: !error,
          error: !empty && error ? error.message : '',
        },
      },
    });
  };

  const handleCardExpiryChange = (event) => {
    const { complete, empty, error } = event;
    dispatch({
      type: 'SET_FIELD',
      payload: {
        fieldName: 'cardExpiry',
        value: {
          completed: complete,
          empty,
          valid: !error,
          error: !empty && error ? error.message : '',
        },
      },
    });
  };

  const handleCardCVCChange = (event) => {
    const { complete, empty, error } = event;
    dispatch({
      type: 'SET_FIELD',
      payload: {
        fieldName: 'cardCVC',
        value: {
          completed: complete,
          empty,
          valid: !error,
          error: !empty && error ? error.message : '',
        },
      },
    });
  };

  const setError = (fieldName, error) => {
    dispatch({
      type: 'SET_FIELD',
      payload: {
        fieldName,
        value: {
          ...state[fieldName],
          valid: false,
          error,
        },
      },
    });
  };

  const clearCardFields = () => dispatch({ type: 'INITIALIZE' });

  /*
  * Calls the Stripe API, passing in the Elements component
  * and other billing details
  */
  const confirmCardPayment = async (clientSecret, name, email) => {
    const response = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name,
          email,
        },
      },
    });
    if (response.error) {
      dispatch({
        type: 'SET_FIELD',
        payload: {
          fieldName: 'cardNumber',
          value: {
            completed: false,
            empty: false,
            valid: false,
            error: response.error.message,
          },
        },
      });
    }
    return response;
  };

  const validatePaymentInfo = () => {
    let valid = true;
    Object.keys(state).forEach((fieldName) => {
      if (!state[fieldName].valid) valid = false;
      if (!state[fieldName].completed) {
        valid = false;
        dispatch({
          type: 'SET_FIELD',
          payload: {
            fieldName,
            value: {
              ...state[fieldName],
              valid: false,
              error: `${fieldNameMapping[fieldName]} IS REQUIRED.`,
            },
          },
        });
      }
    });
    return valid;
  };

  return {
    cardFields: state,
    handleCardNumberChange,
    handleCardExpiryChange,
    handleCardCVCChange,
    setError,
    clearCardFields,
    validatePaymentInfo,
    confirmCardPayment,
  };
};

export default useStripeValidations;
